import { useState } from 'react';
import toast from 'react-hot-toast';
import API from '../services/api';
import { useNavigate } from 'react-router-dom';

function Login({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    organizationName: ''
  });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsPopupOpen, setTermsPopupOpen] = useState(false);

  // Password Strength Calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return '';
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^a-zA-Z0-9]/.test(password)) strength++;
    if (strength <= 2) return 'weak';
    if (strength <= 4) return 'medium';
    return 'strong';
  };

  // Real-time Validation
  const validateField = (name, value) => {
    const errors = { ...validationErrors };
    switch (name) {
      case 'name':
        if (value.length < 2) { errors.name = 'Name must be at least 2 characters'; }
        else { delete errors.name; }
        break;
      case 'organizationName':
        if (value.length < 2) { errors.organizationName = 'Organization name must be at least 2 characters'; }
        else { delete errors.organizationName; }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) { errors.email = 'Please enter a valid email address'; }
        else { delete errors.email; }
        break;
      case 'password':
        if (!isLogin) {
          if (value.length < 8) { errors.password = 'Password must be at least 8 characters'; }
          else if (!/[a-z]/.test(value)) { errors.password = 'Password must contain lowercase letter'; }
          else if (!/[A-Z]/.test(value)) { errors.password = 'Password must contain uppercase letter'; }
          else if (!/[0-9]/.test(value)) { errors.password = 'Password must contain a number'; }
          else { delete errors.password; }
          setPasswordStrength(calculatePasswordStrength(value));
        }
        break;
      default:
        break;
    }
    setValidationErrors(errors);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isLogin && !termsAccepted) {
      setError('Please accept the Terms & Conditions to create an account');
      return;
    }
    if (!isLogin) {
      if (!formData.name || formData.name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
      if (!formData.organizationName || formData.organizationName.trim().length < 2) { setError('Organization name must be at least 2 characters'); return; }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) { setError('Please enter a valid email address'); return; }
      if (formData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
      if (!/[a-z]/.test(formData.password)) { setError('Password must contain at least one lowercase letter'); return; }
      if (!/[A-Z]/.test(formData.password)) { setError('Password must contain at least one uppercase letter'); return; }
      if (!/[0-9]/.test(formData.password)) { setError('Password must contain at least one number'); return; }
    }
    if (Object.keys(validationErrors).length > 0) { setError('Please fix all errors before submitting'); return; }

    const loadingToast = toast.loading(isLogin ? 'Logging in...' : 'Creating your account...');
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name, organizationName: formData.organizationName };

      const response = await API.post(endpoint, payload);
      const { token, user, subscription, hasEverSubscribed } = response.data.data;

      localStorage.setItem('token', token);
      localStorage.setItem('isSuperAdmin', user.isSuperAdmin ? 'true' : 'false');
      localStorage.setItem('userName', user.name || 'User');
      localStorage.setItem('userEmail', user.email || '');

      // ✅ CLEAN & SINGLE SUBSCRIPTION STATUS EVALUATION
      if (subscription && subscription.status === 'active' && !subscription.isExpired) {
        localStorage.setItem('subscriptionStatus', 'active');
        localStorage.setItem('subscriptionPlan', subscription.planName || 'Active Plan');
        localStorage.setItem('subscriptionEndDate', subscription.endDate || '');
      } else if (hasEverSubscribed) {
        // 🟡 Purana user jiska plan expire ho chuka hai
        localStorage.setItem('subscriptionStatus', 'expired');
        localStorage.setItem('subscriptionPlan', 'expired');
        localStorage.setItem('subscriptionEndDate', subscription?.endDate || '');
      } else {
        // 🔴 Bilkul naya user (Redirection lock chalega)
        localStorage.setItem('subscriptionStatus', 'none');
        localStorage.removeItem('subscriptionPlan');
        localStorage.removeItem('subscriptionEndDate');
      }

      toast.success(isLogin ? 'Welcome back! Redirecting...' : 'Account created successfully!', { id: loadingToast });

setTimeout(() => {
  if (user.isSuperAdmin) {
    window.location.href = '/admin/dashboard';
  } else if (subscription && subscription.status === 'active' && !subscription.isExpired) {
    window.location.href = '/dashboard';
  } else {
    window.location.href = '/pricing';
  }
}, 500);

    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Authentication failed';
      toast.dismiss(loadingToast);
      setError(errorMessage);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setValidationErrors({});
    setPasswordStrength('');
    setTermsAccepted(false);
    setFormData({ email: '', password: '', name: '', organizationName: '' });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* ============================================================
          TERMS & CONDITIONS POPUP
          — modal-overlay ke BAHAR, sibling element ke roop mein
          ============================================================ */}
      {termsPopupOpen && (
        <div
          onClick={() => setTermsPopupOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '20px',
              width: '100%',
              maxWidth: '560px',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
              overflow: 'hidden',
            }}
          >
            {/* Popup Header */}
            <div style={{
              background: 'linear-gradient(135deg, #075E54, #128C7E)',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div>
                <div style={{ color: 'white', fontSize: '17px', fontWeight: '700' }}>
                  📋 Terms & Conditions
                </div>
                <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: '12px', marginTop: '3px' }}>
                  ChatTech24 — Last updated May 2025
                </div>
              </div>
              <button
                onClick={() => setTermsPopupOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '18px',
                  width: '34px',
                  height: '34px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >✕</button>
            </div>

            {/* Scrollable Content */}
            <div style={{ overflowY: 'auto', padding: '24px', flex: 1 }}>
              {[
                {
                  title: 'Introduction & Acceptance',
                  body: `Welcome to ChatTech24. By accessing or using our services at chattech24.com, you agree to be bound by these Terms and Conditions and our Privacy Policy.\n\nIf you do not agree with any part of these Terms, you must immediately discontinue use of our Platform. Continued use after any changes constitutes acceptance of the updated Terms.\n\nThese Terms apply to individual users, business users, and administrators managing organizational accounts.`
                },
                {
                  title: 'Service Description',
                  body: `ChatTech24 provides a WhatsApp Business automation platform offering:\n• WhatsApp Business API integration and management\n• Visual Flow Builder for automated conversation sequences\n• AI-powered chatbot (on eligible subscription plans)\n• Contact management and messaging history\n• Analytics and reporting dashboard\n\nService availability is subject to Meta's WhatsApp API availability. We target 99% uptime but do not guarantee uninterrupted service.`
                },
                {
                  title: 'Account Registration & Eligibility',
                  body: `• You must be at least 18 years of age\n• You must provide accurate, complete information during registration\n• You are responsible for maintaining the confidentiality of your account credentials\n• You must notify us immediately of any unauthorized access\n• One person or entity may not maintain more than one active account without prior written consent\n• You must have legitimate ownership of the WhatsApp number you connect`
                },
                {
                  title: 'Subscription, Payments & Refunds',
                  body: `All prices are in Indian Rupees (INR). Subscriptions auto-renew unless cancelled before the renewal date. You will receive a reminder 7 days before renewal.\n\nRefund Policy: Subscription fees are generally non-refundable once the billing cycle has started. Refund requests must be submitted within 7 days of the charge to legal@chattech24.com.\n\nAccounts with failed payments will be suspended after 3 days. Data is retained for 30 days after expiry before permanent deletion.`
                },
                {
                  title: 'WhatsApp API Usage Rules',
                  body: `Permitted Use:\n• Customer support and service notifications\n• Order confirmations and transactional messages\n• Appointment reminders (with user consent)\n\nStrictly Prohibited:\n• Sending spam or bulk messages to non-consenting users\n• Distributing illegal content, hate speech, or harmful material\n• Misrepresenting your identity or business\n• Automated messaging that violates WhatsApp's rate limits\n\nViolations may result in immediate account suspension and reporting to Meta.`
                },
                {
                  title: 'AI Agent Usage',
                  body: `The AI Agent feature is powered by OpenAI's language models. You are fully responsible for all AI-generated responses sent to your customers.\n\n• AI responses may contain errors or inaccuracies — review before deploying\n• The AI Agent must not be configured to produce illegal or deceptive content\n• We are not liable for AI-generated content that is inaccurate or harmful\n• Do not use AI Agent for medical, legal, or financial advice`
                },
                {
                  title: 'Data Privacy & Security',
                  body: `We collect: Account data, WhatsApp message data, usage data, and payment transaction IDs. We do NOT store card details — these are handled by our payment processor.\n\nWe do NOT sell your personal data to third parties.\n\nData Retention:\n• Active account data retained for duration of subscription\n• After deletion: data purged within 30 days\n• Payment records retained for 7 years (legal requirement)\n\nAll data is transmitted over SSL/TLS encryption. API keys are encrypted at rest.`
                },
                {
                  title: 'Your Rights',
                  body: `You have the right to:\n• Access a copy of your personal data\n• Request correction of inaccurate data\n• Request deletion of your data\n• Withdraw consent for data processing\n\nTo exercise these rights, contact: grievance@chattech24.com\nWe respond within 72 hours on business days.`
                },
                {
                  title: 'Prohibited Uses',
                  body: `You must NOT use our Platform to:\n• Send unsolicited commercial communications (spam)\n• Engage in phishing, fraud, or deceptive practices\n• Distribute malware or harmful code\n• Harass, threaten, or abuse any person\n• Conduct illegal gambling or pyramid schemes\n• Scrape or harvest data from the Platform\n• Resell Platform access without written authorization\n\nViolations result in immediate account suspension without refund and possible legal action.`
                },
                {
                  title: 'Intellectual Property',
                  body: `ChatTech24, its logo, platform design, and all software are our exclusive intellectual property. You are granted a limited, non-exclusive license to use the Platform during your active subscription.\n\nYou retain ownership of all content you upload. WhatsApp® is a trademark of Meta Platforms Inc. OpenAI® is a trademark of OpenAI LLC. We are not affiliated with either company.`
                },
                {
                  title: 'Limitation of Liability',
                  body: `Our total liability for any claims shall not exceed the amount paid by you in the 3 months preceding the claim. We are not liable for loss of profits, data, or business interruption.\n\nThe Platform is provided "AS IS" without warranties of any kind. We are not responsible for actions taken by Meta against your WhatsApp Business Account.`
                },
                {
                  title: 'Termination',
                  body: `You may cancel your subscription at any time. Cancellation takes effect at end of the current billing cycle. No refund for unused portion.\n\nWe may suspend or terminate your account immediately if you breach these Terms, engage in fraudulent activity, or if required by law.\n\nAfter termination: your data is retained for 30 days then permanently deleted.`
                },
                {
                  title: 'Grievance & Contact',
                  body: `For complaints, disputes, or legal notices:\n\nEmail: legal@chattech24.com\nGrievance Officer: grievance@chattech24.com\nWorking Hours: Monday–Friday, 10 AM – 6 PM IST\n\nWe acknowledge all complaints within 24 hours and aim to resolve within 15 business days.\n\nThese Terms are governed by the laws of India. Disputes shall be resolved through arbitration under the Arbitration and Conciliation Act, 1996.`
                },
              ].map((section, i) => (
                <div
                  key={i}
                  style={{
                    marginBottom: '20px',
                    paddingBottom: '20px',
                    borderBottom: i < 12 ? '1px solid #f1f5f9' : 'none',
                  }}
                >
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '700',
                    color: '#065f46',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                    <span style={{
                      width: '22px',
                      height: '22px',
                      background: 'linear-gradient(135deg, #25D366, #128C7E)',
                      borderRadius: '50%',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: '800',
                      flexShrink: 0,
                    }}>
                      {i + 1}
                    </span>
                    {section.title}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#475569',
                    lineHeight: '1.75',
                    whiteSpace: 'pre-line',
                  }}>
                    {section.body}
                  </div>
                </div>
              ))}
            </div>

            {/* Popup Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e2e8f0',
              background: '#f8fafc',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px',
              flexShrink: 0,
            }}>
              <button
                onClick={() => setTermsPopupOpen(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '9px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setTermsAccepted(true);
                  setTermsPopupOpen(false);
                }}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #25D366, #128C7E)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  boxShadow: '0 3px 10px rgba(37,211,102,0.3)',
                }}
              >
                ✓ I Agree & Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================
          MAIN LOGIN/SIGNUP MODAL
          ============================================================ */}
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>

          {/* Close Button */}
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Modal Content */}
          <div className="modal-content">

            {/* Left Side - Branding */}
            <div className="modal-branding">
              <div style={{
                background: 'rgba(255,255,255,0.22)',
                borderRadius: '16px',
                padding: '14px 22px',
                margin: '0 auto 20px auto',
                border: '1px solid rgba(255,255,255,0.35)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxWidth: '240px',
                boxSizing: 'border-box'
              }}>
                <img
                  src="/logo.png"
                  alt="ChatTech24"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '65px',
                    objectFit: 'contain',
                    display: 'block',
                    filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.2))',
                  }}
                />
              </div>
              <p className="modal-brand-subtitle">
                AI-powered WhatsApp automation for modern businesses
              </p>
              <div className="modal-features">
                <div className="modal-feature">
                  <span className="modal-feature-icon">⚡</span>
                  <span>Setup in 5 minutes</span>
                </div>
                <div className="modal-feature">
                  <span className="modal-feature-icon">🎯</span>
                  <span>No coding required</span>
                </div>
                <div className="modal-feature">
                  <span className="modal-feature-icon">🔒</span>
                  <span>Enterprise-grade security</span>
                </div>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="modal-form-section">
              <div className="modal-form-header">
                <h3 className="modal-form-title">
                  {isLogin ? 'Welcome Back' : 'Create Account'}
                </h3>
                <p className="modal-form-subtitle">
                  {isLogin ? 'Sign in to your account' : 'Get started in seconds'}
                </p>
              </div>

              {error && (
                <div className="modal-error">
                  <span>{error}</span>
                  <button onClick={() => setError('')} className="modal-error-close">✕</button>
                </div>
              )}

              <form onSubmit={handleSubmit}>

                {/* Signup-only fields */}
                {!isLogin && (
                  <>
                    <div className="modal-input-group">
                      <label className="modal-label">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className={`modal-input ${validationErrors.name ? 'modal-input-error' : ''}`}
                        required
                      />
                      {validationErrors.name && (
                        <span className="modal-input-error-text">{validationErrors.name}</span>
                      )}
                    </div>

                    <div className="modal-input-group">
                      <label className="modal-label">Organization Name *</label>
                      <input
                        type="text"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleInputChange}
                        placeholder="My Company"
                        className={`modal-input ${validationErrors.organizationName ? 'modal-input-error' : ''}`}
                        required
                      />
                      {validationErrors.organizationName && (
                        <span className="modal-input-error-text">{validationErrors.organizationName}</span>
                      )}
                    </div>
                  </>
                )}

                {/* Email */}
                <div className="modal-input-group">
                  <label className="modal-label">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    className={`modal-input ${validationErrors.email ? 'modal-input-error' : ''}`}
                    required
                  />
                  {validationErrors.email && (
                    <span className="modal-input-error-text">{validationErrors.email}</span>
                  )}
                </div>

                {/* Password */}
                <div className="modal-input-group">
                  <label className="modal-label">Password *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      className={`modal-input ${validationErrors.password ? 'modal-input-error' : ''}`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: '12px', top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '18px', color: '#94a3b8',
                      }}
                    >
                      {showPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  {validationErrors.password && (
                    <span className="modal-input-error-text">{validationErrors.password}</span>
                  )}

                  {/* Password Strength */}
                  {!isLogin && formData.password && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          width: passwordStrength === 'weak' ? '33%' : passwordStrength === 'medium' ? '66%' : '100%',
                          background: passwordStrength === 'weak' ? '#ef4444' : passwordStrength === 'medium' ? '#f59e0b' : '#22c55e',
                          transition: 'all 0.3s ease',
                        }} />
                      </div>
                      <span style={{
                        fontSize: '11px',
                        color: passwordStrength === 'weak' ? '#ef4444' : passwordStrength === 'medium' ? '#f59e0b' : '#22c55e',
                        marginTop: '4px', display: 'block',
                      }}>
                        Password strength: {passwordStrength || 'weak'}
                      </span>
                    </div>
                  )}

                  {/* Password Requirements */}
                  {/* Password Requirements — 2 Left, 2 Right Grid */}
                  {/* Password Requirements — Bold Heading */}
                  {!isLogin && (
                    <div style={{ marginTop: '6px', fontSize: '11px', color: '#64748b' }}>
                      {/* 💥 Updated Bold & Highlighted Heading */}
                      <div style={{
                        marginBottom: '3px',
                        fontWeight: '700',
                        fontSize: '11.5px',
                        color: '#334155',
                        letterSpacing: '0.2px'
                      }}>
                        Password must contain:
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 8px' }}>
                        <span style={{ color: formData.password.length >= 8 ? '#22c55e' : '#64748b' }}>
                          {formData.password.length >= 8 ? '✓' : '○'} At least 8 characters
                        </span>
                        <span style={{ color: /[a-z]/.test(formData.password) ? '#22c55e' : '#64748b' }}>
                          {/[a-z]/.test(formData.password) ? '✓' : '○'} One lowercase letter
                        </span>
                        <span style={{ color: /[A-Z]/.test(formData.password) ? '#22c55e' : '#64748b' }}>
                          {/[A-Z]/.test(formData.password) ? '✓' : '○'} One uppercase letter
                        </span>
                        <span style={{ color: /[0-9]/.test(formData.password) ? '#22c55e' : '#64748b' }}>
                          {/[0-9]/.test(formData.password) ? '✓' : '○'} One number
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password — Signup only */}

                {/* Terms Checkbox — Signup only */}
                {!isLogin && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '6px',
                    marginBottom: '8px',
                    padding: '6px 10px',
                    background: '#f0fdf9',
                    borderRadius: '8px',
                    border: '1px solid rgba(37,211,102,0.25)',
                  }}>
                    <input
                      type="checkbox"
                      id="terms-checkbox"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      style={{
                        width: '15px',
                        height: '15px',
                        cursor: 'pointer',
                        accentColor: '#128C7E',
                        flexShrink: 0,
                      }}
                    />
                    <label htmlFor="terms-checkbox" style={{
                      fontSize: '11px',
                      color: '#475569',
                      lineHeight: '1.3',
                      cursor: 'pointer',
                    }}>
                      I agree to the{' '}
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setTermsPopupOpen(true);
                        }}
                        style={{
                          color: '#128C7E',
                          fontWeight: '700',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        Terms & Conditions
                      </span>
                    </label>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  className="modal-submit"
                  disabled={!isLogin && !termsAccepted}
                  style={{
                    opacity: (!isLogin && !termsAccepted) ? 0.5 : 1,
                    cursor: (!isLogin && !termsAccepted) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isLogin ? 'Sign In' : 'Create Account'}
                </button>
              </form>

              {/* T&C note for Login mode */}
              {isLogin && (
                <p style={{
                  textAlign: 'center',
                  fontSize: '11px',
                  color: '#94a3b8',
                  marginTop: '12px',
                  lineHeight: '1.6',
                }}>
                  By signing in, you agree to our{' '}
                  <span
                    onClick={() => setTermsPopupOpen(true)}
                    style={{
                      color: '#128C7E',
                      fontWeight: '600',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    Terms & Conditions
                  </span>
                </p>
              )}

              {/* Toggle Login / Signup */}
              <div className="modal-toggle">
                <span className="modal-toggle-text">
                  {isLogin ? "Don't have an account?" : 'Already have an account?'}
                </span>
                <button onClick={toggleMode} className="modal-toggle-btn">
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;